import { useStore } from '@nanostores/react';
import {
  List as TabsList,
  Root as TabsRoot,
  Trigger as TabsTrigger,
  type TabsTriggerProps,
} from '@radix-ui/react-tabs';
import { atom } from 'nanostores';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { OwnerSelector, type OwnerOption } from './OwnerSelector';
import { Button } from '~/components/ui/Button';
import { Input } from '~/components/ui/Input';
import { Dialog, DialogDescription, DialogTitle, type DialogProps } from '~/components/ui/LegacyDialog';
import { Select } from '~/components/ui/Select';
import { useGithubToken, type GetGithubToken } from '~/lib/client-github';
import { useGithubStore } from '~/lib/context/github';
import {
  getOrganizations,
  getRepositories,
  getRepository,
  getRepositoryByName,
  getRepositoryId,
  GraphQLError,
  type Organization,
} from '~/lib/github-api';
import { useDebounce } from '~/lib/hooks/useThrottle';
import { userStore } from '~/lib/stores/user';
import type { RepoFullName } from '~/types/github';
import { classNames } from '~/utils/classNames';
import { useInitRef } from '~/utils/init-ref';
import { invariant } from '~/utils/invariant';
import { waitUntil } from '~/utils/nanostore';
import { wait } from '~/utils/promises';

// 10 MiB
const MAX_SIZE_IN_KB = 10 * 2 ** 10;

interface RepoError {
  message: string;
  fatal: boolean;
}

export function ImportRepositoryDialog({
  open,
  onClose,
  onChoose,
}: {
  open: boolean;
  onClose: DialogProps['onClose'];
  onChoose: (repo: RepoFullName) => Promise<void>;
}) {
  const [repos, setRepos] = useState<string[]>();

  const github = useGithubStore();
  const getGithubToken = useGithubToken();

  useEffect(() => {
    github.on('connected', () => {
      fetchRepos();
    });
  }, []);

  async function fetchRepos() {
    const token = await getGithubToken();

    if (!token) {
      return;
    }

    const repositories = await getRepositories(token);

    setRepos(repositories.map((repo) => repo.name));
  }

  return open && <ImportRepositoryDialogInner onClose={onClose} onChoose={onChoose} repos={repos ?? []} />;
}

type RepoState = 'valid' | 'not_found' | 'unauthorized' | 'no_branch' | 'too_large' | undefined;

function ImportRepositoryDialogInner({
  onClose,
  onChoose,
  repos,
}: {
  onClose: DialogProps['onClose'];
  onChoose: (repo: RepoFullName) => Promise<void>;
  repos: string[];
}) {
  const [selectedRepo, setSelectedRepo] = useState<RepoFullName>();
  const user = useStore(userStore.user);
  const [busy, setBusy] = useState(false);

  const repoStateStoreRef = useInitRef(() => atom<RepoState>('valid'));
  const repoStateStore = repoStateStoreRef.current;
  const repoState = useStore(repoStateStore);

  const getGithubToken = useGithubToken();
  const github = useGithubStore();
  const username = useStore(github.username);
  const [reposByOwner, setReposByOwner] = useState({ [username!]: repos });
  const [orgs, setOrgs] = useState<Organization[]>([]);

  const githubOrgs = user?.featureFlags.githubOrgs;

  const owners = useMemo(() => {
    const options: OwnerOption[] = [
      { username: username!, user, id: '__dummy__' },
      ...orgs.map((org) => ({
        id: org.id,
        username: org.login,
        user: {
          name: org.login,
          avatar: org.avatarUrl,
        },
      })),
    ];

    if (!githubOrgs) {
      return options[0];
    }

    return options;
  }, [username, user, orgs, githubOrgs]);

  const [owner, setOwner] = useState(Array.isArray(owners) ? owners[0] : owners);

  if (import.meta.env.DEV) {
    invariant(username);
  }

  const selectedRepoRef = useRef<RepoFullName>();

  useEffect(() => {
    selectedRepoRef.current = selectedRepo;
  });

  useEffect(() => {
    async function fetchOrganizations() {
      if (!githubOrgs) {
        return;
      }

      const token = await getGithubToken();

      if (!token) {
        return;
      }

      const orgs = await getOrganizations(token);

      setOrgs(orgs);
    }

    fetchOrganizations();
  }, [githubOrgs]);

  const updateRepoPermissions = useCallback(async (repo: RepoFullName) => {
    const state = await checkRepoPermissions(getGithubToken, repo).catch(() => 'valid' as const);

    if (selectedRepoRef.current === repo) {
      repoStateStore.set(state);
    }
  }, []);

  const handleRepoUpdate = useDebounce(updateRepoPermissions, 300);

  async function handleChange(repo?: RepoFullName) {
    setSelectedRepo(repo);

    if (import.meta.env.DEV) {
      invariant(!busy);
    }

    if (!repo) {
      repoStateStore.set('valid');

      return;
    }

    repoStateStore.set(undefined);
    handleRepoUpdate(repo);
  }

  async function handleChoose() {
    if (!selectedRepo) {
      return;
    }

    setBusy(true);

    const repoState = await waitUntil(repoStateStore, (value) => value !== undefined);

    if (!(repoState === 'valid' || repoState === 'too_large')) {
      setBusy(false);

      return;
    }

    if (repoState === 'too_large') {
      await wait(500);
    }

    try {
      await onChoose(selectedRepo);
    } finally {
      setBusy(false);
    }
  }

  let errorMessage: RepoError | undefined;

  switch (repoState) {
    case 'not_found': {
      errorMessage = { message: 'Cannot find this repository.', fatal: true };
      break;
    }
    case 'unauthorized': {
      errorMessage = { message: 'You do not have push permissions for this repository.', fatal: true };
      break;
    }
    case 'no_branch': {
      errorMessage = { message: 'Cannot find a default branch. Is the repository empty?', fatal: true };
      break;
    }
    case 'too_large': {
      errorMessage = { message: 'Repository size might be too large', fatal: false };
      break;
    }
  }

  async function handleOwnerChange(newOwner: OwnerOption) {
    setOwner(newOwner);

    if (reposByOwner[newOwner.username]) {
      return;
    }

    const token = await getGithubToken();

    if (!token) {
      return;
    }

    const repositories = await getRepositories({ organization: newOwner.username }, token);

    setReposByOwner((current) => ({ ...current, [newOwner.username]: repositories.map(({ name }) => name) }));
  }

  // FIXME: no repos scenario
  return (
    username && (
      <ImportRepositoryDialogControlled
        {...{ username, busy, owner }}
        selectedRepo={selectedRepo}
        owners={owners}
        onOwnerChange={handleOwnerChange}
        repos={reposByOwner[owner.username] ?? []}
        onChange={handleChange}
        onClose={onClose}
        onChoose={handleChoose}
        error={errorMessage}
      />
    )
  );
}

interface TabProps extends Omit<TabsTriggerProps, 'children'> {
  active: boolean;
  children: ReactNode;
}

const Tab = memo<TabProps>(({ value, active, children, ...props }) => {
  return (
    <TabsTrigger
      value={value}
      className={classNames('text-sm px-2.5 py-1 rounded-md', {
        'bg-bolt-elements-background-depth-4 text-bolt-elements-textTertiary hover:text-bolt-elements-textPrimary':
          !active,
        'bg-bolt-elements-button-primaryLight-background text-bolt-elements-button-primaryLight-text': active,
      })}
      {...props}
    >
      {children}
    </TabsTrigger>
  );
});

export function ImportRepositoryDialogControlled({
  selectedRepo,
  owner,
  owners,
  onOwnerChange,
  onClose,
  onChoose,
  onChange,
  repos,
  busy,
  error,
}: {
  owner: OwnerOption;
  owners: OwnerOption[] | OwnerOption;
  onOwnerChange: (owner: OwnerOption) => void;
  selectedRepo: RepoFullName | undefined;
  onChange: (repo?: RepoFullName) => void;
  onClose: DialogProps['onClose'];
  onChoose: () => void;
  repos: string[];
  busy: boolean;
  error?: RepoError;
}) {
  const [inputMethod, setInputMethod] = useState<'select' | 'url'>('select');
  const [repoUrl, setRepoUrl] = useState('');
  const [urlError, setUrlError] = useState<string>();

  const validateGithubUrl = (url: string): RepoFullName | undefined => {
    const githubUrlPattern = /^https:\/\/github\.com\/([^/]+)\/([^/]+)\/?$/;

    if (!url) {
      setUrlError('URL is required');

      return undefined;
    }

    const match = url.match(githubUrlPattern);

    if (!match) {
      setUrlError('Please enter a valid GitHub repository URL');

      return undefined;
    }

    const [, owner, rawName] = match;
    setUrlError(undefined);

    const name = rawName.replace(/\.git$/, '');

    return { owner, name };
  };

  const handleUrlChange = (url: string) => {
    setRepoUrl(url);

    const repoInfo = validateGithubUrl(url);

    onChange(repoInfo);
  };

  const handleChoose = () => {
    if (selectedRepo?.name) {
      onChoose();
    }
  };

  const handleOwnerChange = (newOwner: OwnerOption) => {
    onOwnerChange(newOwner);
    onChange();
  };

  return (
    <Dialog onClose={onClose} open maxWidth={600}>
      <DialogTitle className="p-6 font-bold text-lg border-b-1px border-bolt-elements-borderColor no-outline">
        <span className="flex items-center gap-1 text-lg">
          <span className="i-bolt:logos-github?mask text-2xl"></span>
          <span className="font-semibold">Import a repository</span>
        </span>
      </DialogTitle>
      <DialogDescription>
        <div className="flex flex-col w-full items-start p-2">
          <div className="mb-3">
            <TabsRoot
              value={inputMethod}
              onValueChange={(value: string) => {
                const method = value as 'select' | 'url';

                setInputMethod(method);

                if (method === 'select') {
                  onChange();
                } else {
                  setRepoUrl('');
                  setUrlError('');
                  onChange();
                }
              }}
            >
              <TabsList className="flex gap-2.5 mb-2.5">
                <Tab disabled={busy} value="select" active={inputMethod === 'select'}>
                  Your repositories
                </Tab>
                <Tab disabled={busy} value="url" active={inputMethod === 'url'}>
                  Import from URL
                </Tab>
              </TabsList>
            </TabsRoot>
          </div>

          {inputMethod === 'select' ? (
            <>
              <label
                htmlFor="github-import-repo-dialog-repo-select"
                className="text-bolt-elements-textSecondary mb-3 text-sm"
              >
                Choose an existing repository
              </label>

              <div className="flex gap-5 w-full justify-between items-center mb-1 min-h-11">
                <OwnerSelector
                  id="github-import-repo-dialog-owner-select"
                  owners={owners}
                  owner={owner}
                  onChange={handleOwnerChange}
                />
                /
                <Select
                  id="github-import-repo-dialog-repo-select"
                  placeholder="choose a repository"
                  containerClassName="w-initial grow"
                  contentClassName="z-dialog"
                  options={repos.map((repo) => ({ label: repo, value: repo }))}
                  disabled={busy}
                  onValueChange={(value) => onChange({ owner: owner.username, name: value })}
                />
              </div>
            </>
          ) : (
            <div className="w-full mb-1 min-h-11">
              <label
                htmlFor="github-import-repo-dialog-url"
                className="text-bolt-elements-textSecondary mb-3 text-sm block"
              >
                Enter GitHub repository URL
              </label>
              <Input
                id="github-import-repo-dialog-url"
                placeholder="https://github.com/username/repository"
                value={repoUrl}
                disabled={busy}
                onChange={(e) => handleUrlChange(e.target.value)}
                className="w-full"
                error={urlError || error?.message}
                warning={error?.fatal === false}
              />
            </div>
          )}

          <p
            className={classNames('text-right w-full text-sm mb-4', {
              'text-bolt-elements-item-contentDanger': error?.fatal,
              'text-bolt-elements-item-contentWarn': error?.fatal === false,
            })}
          >
            {(inputMethod === 'select' ? error?.message : undefined) ?? <>&nbsp;</>}
          </p>

          <div className="flex w-full justify-between items-center">
            <div className="text-sm text-bolt-elements-textTertiary">
              New to GitHub?{' '}
              <a className="text-inherit!" href="https://support.bolt.new/integrations/git" target="_blank">
                Learn more
              </a>
            </div>
            <Button
              size="lg"
              disabled={!selectedRepo?.name || error?.fatal || busy}
              onClick={handleChoose}
              variant="primary"
            >
              <div className="flex items-center">
                {busy && <div className="i-svg-spinners:90-ring-with-bg size-4 mr-1" />}
                <div>{busy ? 'Importing repository' : 'Choose this repository'}</div>
              </div>
            </Button>
          </div>
        </div>
      </DialogDescription>
    </Dialog>
  );
}

async function checkRepoPermissions(getGithubToken: GetGithubToken, repo: RepoFullName) {
  const token = await getGithubToken();

  if (!token) {
    throw new Error('Cannot check permissions');
  }

  let repositoryId: string;

  try {
    repositoryId = await getRepositoryId(repo, token);
  } catch (error) {
    if (error instanceof GraphQLError && error.contains('NOT_FOUND')) {
      return 'not_found';
    }

    throw error;
  }

  const [{ viewerPermission: permission, defaultBranchRef }, { size }] = await Promise.all([
    getRepository(repositoryId, token),
    getRepositoryByName(repo, token),
  ]);

  const canPush = permission === 'ADMIN' || permission === 'MAINTAIN' || permission === 'WRITE';

  if (!canPush) {
    return 'unauthorized';
  }

  if (!defaultBranchRef) {
    return 'no_branch';
  }

  if (size > MAX_SIZE_IN_KB) {
    return 'too_large';
  }

  return 'valid';
}
